import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getTableQueries, getTableQueriesFilters } from "@/helpers/apis";
import { useAppState } from "@/modules/app/useAppContext";
import DataTable from "@/components/tables/DataTable";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import { Paginate } from "@/components/paginate";
import { useEffect, useState } from "react";
import { TableQuery } from "../types";
import dayjs from "dayjs";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useTableQueriesFilters } from "@/pages/datasets/hooks/useTableQueriesFilters";
import { Stack } from "@altimateai/lego";
import { DateRange, StaticDateRangeKeys } from "@/components/dateRange";
import { FilterApplicableFor } from "@/components/tags/types";
import { useDatatableContext } from "@/components/tables/contexts/DatatableContextProvider";
import { getTableQueriesColumns } from "./tableQueriesColumns";

interface TableQueriesProps {
  tableId: string;
}

const TableQueries = ({ tableId }: TableQueriesProps) => {
  const navigate = useNavigate();
  const { currency, datastores } = useAppState();
  const { getFilterValue, registerFilters } = useDatatableContext();
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const { updateSearchParamsByKey } = useFiltersBySearchParams();
  const { filterParams, isInitialized } = useTableQueriesFilters({
    sortAttribute: "query_cost",
    sortOrder: "desc",
  });

  const { page, pageSize, setPage, setPageSize: updatePageSize } = useTablePagination();

  // Register date filters
  useEffect(() => {
    registerFilters({
      startDate: {
        label: "Start Date",
        defaultValue: dayjs().subtract(28, "day").toDate(),
      },
      endDate: {
        label: "End Date",
        defaultValue: dayjs().toDate(),
      },
    });
  }, [registerFilters]);

  // Get current date filters (handle both Date objects and ISO strings from search params)
  const startDateValue = getFilterValue("startDate");
  const endDateValue = getFilterValue("endDate");

  const startDate = startDateValue
    ? startDateValue instanceof Date
      ? startDateValue
      : new Date(startDateValue as string)
    : dayjs().subtract(28, "day").toDate();

  const endDate = endDateValue
    ? endDateValue instanceof Date
      ? endDateValue
      : new Date(endDateValue as string)
    : dayjs().toDate();

  const { data: queries, isFetching: isQueriesLoading } = useQuery({
    queryKey: ["table_queries", filterParams],
    queryFn: () => getTableQueries(tableId, filterParams),
    enabled: isInitialized, // Only fetch when URL sync is complete
  });

  // Handle successful queries data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (queries) {
      setTotalPages(queries.pages || 0);
      setTotalItems(queries.total || 0);
    }
  }, [queries]);

  const { data: filters, isFetching: isFiltersLoading } = useQuery({
    queryKey: ["table_queries_filters", filterParams.start_date, filterParams.end_date],
    queryFn: () =>
      getTableQueriesFilters(tableId, {
        startDate: filterParams.start_date,
        endDate: filterParams.end_date,
      }),
  });

  const getOptions = (data?: string[]) => {
    if (!data || data.length === 0) {
      return [];
    }
    return data.map(item => ({ label: item, value: item }));
  };

  const headers = getTableQueriesColumns({
    getOptions,
    filters,
    currency,
    datastores: datastores || [],
  });

  return (
    <>
      <Stack justify={"between"} align={"center"} className={"al-mb-4"}>
        <div id={"filter-container"} />
        <DateRange
          startDate={startDate}
          endDate={endDate}
          onDateRangeSelect={(startDate, endDate) =>
            updateSearchParamsByKey({
              startDate: startDate.toISOString(),
              endDate: endDate.toISOString(),
            })
          }
          availableStaticRanges={[
            StaticDateRangeKeys.LAST_DAY,
            StaticDateRangeKeys.LAST_7_DAYS,
            StaticDateRangeKeys.LAST_28_DAYS,
          ]}
          disableCalendarSelection
        />
      </Stack>
      <DataTable
        minWidth={2400}
        header={headers}
        applicableFor={FilterApplicableFor.TABLE_QUERIES}
        portals={{
          filter: "filter-container",
        }}
        data={queries || { items: [] }}
        isLoading={isQueriesLoading || isFiltersLoading}
        onRowClick={(item: TableQuery) => navigate(`/query/${item.query_id}`)}
        defaultSort={{ sortParamValue: "query_cost", order: "desc" }}
      />
      <Paginate
        itemCount={totalItems}
        page={page}
        pageSize={pageSize}
        numPages={totalPages}
        onPageClick={setPage}
        onPageSizeChange={updatePageSize}
        showPageSize
        isLoading={isQueriesLoading}
      />
    </>
  );
};

export default TableQueries;
